// Pure rules for the journalist-profile review queue (Comptes Média).
//
// The decision is taken by the `review-media-profile` edge function (this
// repo); these helpers decide what the queue shows and which buttons exist, so
// the UI never offers a transition the function answers PROFILE_NOT_REVIEWABLE
// or NO_ORGANIZATION to. Keep them in step with
// supabase/functions/_shared/mediaProfileReview.ts.

export type ProfileStatus = "pending" | "accepted" | "rejected";
export type ProfileReviewAction = "accept" | "reject";

/** Same limit as MAX_REASON in review-media-profile. */
export const PROFILE_REASON_MAX = 1000;

export interface ProfileRowLike {
  lirf_status?: string | null;
  is_verified?: boolean | null;
  rejection_reason?: string | null;
  user_id?: string | null;
  organization_id?: string | null;
}

/**
 * lirf_status is canonical (the mobile guard mirrors is_verified from it).
 * The legacy columns only decide for a row that has no lirf_status at all.
 */
export function profileStatus(p: ProfileRowLike): ProfileStatus {
  const s = (p.lirf_status ?? "").toLowerCase();
  if (s === "accepted" || s === "rejected" || s === "pending") return s;
  if (p.is_verified) return "accepted";
  if (p.rejection_reason) return "rejected";
  return "pending";
}

/** No organization: a freelancer, disabled in the org-only model. */
export function isFreelance(p: ProfileRowLike): boolean {
  return !p.organization_id;
}

/**
 * A pending profile that already owns a login was accepted before and edited
 * since by its organization (the guard sends it back to pending and clears
 * verified_at, so the login is the only surviving trace). Misses an accepted
 * journalist who never got a login — see the `last_accepted_at` backend request.
 */
export function isReReview(p: ProfileRowLike): boolean {
  return profileStatus(p) === "pending" && !!p.user_id;
}

/** What the queue, the tab badge and the bell count as "to review". */
export function isPendingProfile(p: ProfileRowLike): boolean {
  return profileStatus(p) === "pending" && !isFreelance(p);
}

export function availableProfileActions(p: ProfileRowLike): ProfileReviewAction[] {
  if (isFreelance(p)) return [];
  switch (profileStatus(p)) {
    case "pending":
      return ["accept", "reject"];
    case "accepted":
      return ["reject"];
    case "rejected":
      return ["accept"];
  }
}

export function validateProfileDecision(
  action: ProfileReviewAction,
  reason: string | null | undefined,
): "REASON_REQUIRED" | "TEXT_TOO_LONG" | null {
  if (action !== "reject") return null;
  const trimmed = (reason ?? "").trim();
  if (trimmed.length > PROFILE_REASON_MAX) return "TEXT_TOO_LONG";
  if (!trimmed) return "REASON_REQUIRED";
  return null;
}

export interface ProfileReviewAccount {
  created: boolean;
  email_sent: boolean;
  email_error?: string | null;
  code?: string | null;
}

export interface ProfileReviewResult {
  status: "accepted" | "rejected";
  account: ProfileReviewAccount;
}

export type ProfileReviewStep =
  | "accepted"
  | "rejected"
  | "login_created"
  | "login_existing"
  | "login_failed"
  | "email_sent"
  | "email_failed";

export interface ProfileReviewSummary {
  tone: "success" | "warning" | "neutral";
  steps: ProfileReviewStep[];
  /** Offer "Resend credentials" (provision-journalist-account). */
  canResend: boolean;
  /** Provisioning error code, for api_errors.<code>. */
  code: string | null;
}

// Retrying cannot fix these: the profile (e-mail, organization) or the login
// itself must change first.
const NOT_RETRYABLE = new Set(["NO_EMAIL", "EMAIL_IN_USE", "NOT_ELIGIBLE", "NO_ORGANIZATION", "SHARED_LOGIN"]);

/**
 * Result panel of an acceptance: "accepted · login created · e-mail sent", or
 * what went wrong. The acceptance itself always stands.
 */
export function summarizeProfileReview(r: ProfileReviewResult): ProfileReviewSummary {
  if (r.status === "rejected") {
    return { tone: "neutral", steps: ["rejected"], canResend: false, code: null };
  }
  const a = r.account;
  if (a.created) {
    return a.email_sent
      ? { tone: "success", steps: ["accepted", "login_created", "email_sent"], canResend: true, code: null }
      : { tone: "warning", steps: ["accepted", "login_created", "email_failed"], canResend: true, code: null };
  }
  if (a.code) {
    return {
      tone: "warning",
      steps: ["accepted", "login_failed"],
      canResend: !NOT_RETRYABLE.has(a.code),
      code: a.code,
    };
  }
  // Re-review: the login already exists, nothing was sent (by design).
  return { tone: "success", steps: ["accepted", "login_existing"], canResend: true, code: null };
}

// ----------------------------------------------------------------------------
// The journalist's fiche, as seen from an accreditation demand
// ----------------------------------------------------------------------------

/** Fiche chip of a journalist in a demand; `suspended` outranks the review. */
export type JournalistFicheState = ProfileStatus | "suspended";

export interface FicheRowLike extends ProfileRowLike {
  participation_suspended?: boolean | null;
}

/**
 * accepted / pending / rejected (profileStatus), or `suspended` when the
 * federation suspended the journalist's participation — whatever the review
 * says, a suspended journalist must not be accredited.
 */
export function journalistFicheState(p: FicheRowLike): JournalistFicheState {
  if (p.participation_suspended) return "suspended";
  return profileStatus(p);
}

/**
 * Approving this journalist for a match is risky: the fiche (identity, press
 * card) has not been accepted, or the participation is suspended. The server
 * does not block it; the drawer warns.
 */
export function isRiskyAccreditation(p: FicheRowLike | null | undefined): boolean {
  if (!p) return true;
  return journalistFicheState(p) !== "accepted";
}
